import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { devLog, devError } from '../../utils/devLogger';
import { approvePayment } from '../../lib/api/payment/paymentApi';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import PrimaryButton from '../../components/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton';
import { toast } from 'react-toastify';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
`;

const IconContainer = styled.div<{ $success?: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin-bottom: 24px;
  background: ${props => props.$success ? '#e8f5e8' : '#fee'};
  color: ${props => props.$success ? '#22c55e' : '#ef4444'};
`;

const Title = styled.h1<{ $success?: boolean }>`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${props => props.$success ? '#22c55e' : '#ef4444'};
`;

const Message = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  line-height: 1.5;
`;

const DetailSection = styled.div`
  width: 100%;
  background: #f9f9f9;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const DetailLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #7a6647;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface PaymentData {
  paymentKey?: string;
  orderId: string;
  amount: number;
  orderName?: string;
  customerName?: string;
  method?: string;
}

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const confirmPayment = async () => {
      try {
        const paymentKey = searchParams.get('paymentKey');
        const orderId = searchParams.get('orderId');
        const amount = searchParams.get('amount');
        const orderName = searchParams.get('orderName');

        devLog('결제 승인 시작:', { paymentKey, orderId, amount, orderName });

        if (!paymentKey || !orderId || !amount) {
          throw new Error('결제 정보가 올바르지 않습니다.');
        }

        // 결제 승인 API 호출
        const approvalResult = await approvePayment({
          paymentKey: paymentKey,
          orderId: orderId,
          amount: Number(amount)
        });

        devLog('결제 승인 완료:', approvalResult);

        // 승인 성공 시 결제 데이터 설정
        const paymentData: PaymentData = {
          paymentKey: paymentKey,
          orderId: orderId,
          amount: Number(amount),
          orderName: orderName || '상품 구매',
          customerName: '구매자',
          method: '카드' // TOSS 결제에서는 카드/계좌이체만 지원
        };
        
        setPaymentData(paymentData);
        toast.success('결제가 완료되었습니다!');

      } catch (error: any) {
        devError('결제 승인 실패:', error);
        setError(error.message || '결제 승인에 실패했습니다.');
        toast.error('결제 승인 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    confirmPayment();
  }, [searchParams]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToOrders = () => {
    navigate('/my-page/orders');
  };

  const handleShowReceipt = () => {
    // 실제 환경에서는 서버에서 영수증 URL을 제공받아야 함
    alert('영수증 기능은 서버 API 연동 후 사용 가능합니다.');
  };

  if (loading) {
    const LoadingView = (
      <MainLayout>
        <CenteredLayout>
          <LoadingContainer>
            <Spinner />
            <p>결제를 확인하고 있습니다...</p>
          </LoadingContainer>
        </CenteredLayout>
      </MainLayout>
    );
    
    return (
      <ResponsiveView
        mobileView={LoadingView}
        desktopView={LoadingView}
      />
    );
  }

  if (error || !paymentData) {
    const ErrorView = (
      <MainLayout>
        <CenteredLayout>
          <Container>
            <IconContainer $success={false}>❌</IconContainer>
            <Title $success={false}>결제 확인 실패</Title>
            <Message>
              {error || '결제 정보를 확인할 수 없습니다.'}
            </Message>
            <ButtonContainer>
              <PrimaryButton onClick={handleGoHome}>
                홈으로 돌아가기
              </PrimaryButton>
            </ButtonContainer>
          </Container>
        </CenteredLayout>
      </MainLayout>
    );
    
    return (
      <ResponsiveView
        mobileView={ErrorView}
        desktopView={ErrorView}
      />
    );
  }

  const SuccessView = (
    <MainLayout>
      <CenteredLayout>
        <Container>
          <IconContainer $success={true}>✅</IconContainer>
          <Title $success={true}>결제 완료</Title>
          <Message>
            구매가 정상적으로 완료되었습니다.
          </Message>

          <DetailSection>
            <DetailRow>
              <DetailLabel>주문명</DetailLabel>
              <DetailValue>{paymentData.orderName || '상품 구매'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>주문번호</DetailLabel>
              <DetailValue>{paymentData.orderId}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>결제금액</DetailLabel>
              <DetailValue>{paymentData.amount.toLocaleString()}원</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>결제수단</DetailLabel>
              <DetailValue>{paymentData.method || '카드'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>결제일시</DetailLabel>
              <DetailValue>
                {new Date().toLocaleString('ko-KR')}
              </DetailValue>
            </DetailRow>
          </DetailSection>

          <ButtonContainer>
            <PrimaryButton onClick={handleGoToOrders} >
              주문 내역 보기
            </PrimaryButton>
            <SecondaryButton onClick={handleShowReceipt} fullWidth>
              영수증 보기
            </SecondaryButton>
            <SecondaryButton onClick={handleGoHome} fullWidth>
              홈으로 돌아가기
            </SecondaryButton>
          </ButtonContainer>
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={SuccessView}
      desktopView={SuccessView}
    />
  );
};

export default PaymentSuccess;